import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { useShallow } from "zustand/react/shallow";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";
import { useDashboardStore, type PositionLive } from "@/store/dashboard.store";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import { terminalPath, type TerminalMarket } from "@/lib/market";
import type { PairListItem } from "@/lib/api/pairs";

/**
 * Open positions on sibling symbol pairs of the active account-pair.
 *
 * The terminal's primary positions table is per-symbol-pair (scoped to
 * `pair.id`), which means a XAUUSD ticket opened on the seed pair is
 * invisible from /terminal/<eurusd-sibling-slug>. When the operator
 * adds new symbols on /pairs/:slug they often return to the terminal
 * via a sibling slug and feel like the gold position "disappeared".
 *
 * This strip mounts one WS subscriber per sibling pair, surfaces any
 * open position belonging to the legs of the active account-pair, and
 * links back to that pair's terminal so the operator can jump to the
 * exact context where the position is "primary". Rendering nothing
 * when there are no sibling positions keeps the row off the page in
 * the common case.
 */
export function SiblingPositionsStrip({
  pair,
  allPairs,
  accountIds,
  firmNameFor,
  market,
}: {
  pair: PairListItem;
  allPairs: PairListItem[] | null;
  accountIds: Set<string>;
  firmNameFor(accountId: string): string;
  market: TerminalMarket;
}): ReactNode {
  const siblings = useMemo<PairListItem[]>(() => {
    if (!allPairs || !pair.accountPairSlug) return [];
    return allPairs.filter(
      (p) => p.accountPairSlug === pair.accountPairSlug && p.id !== pair.id,
    );
  }, [allPairs, pair.accountPairSlug, pair.id]);

  const siblingIds = useMemo(() => siblings.map((sp) => sp.id), [siblings]);
  // Shallow-equal selector: each sibling's `positions` slice is the
  // SAME reference across renders unless that pair actually got a
  // position event, so a shallow check is the right granularity. A
  // fresh object literal under Object.is would render-loop.
  const positionsByPair = useDashboardStore(
    useShallow((s) => {
      const out: Record<string, Record<string, PositionLive> | null> = {};
      for (const id of siblingIds) {
        out[id] = s.pairs[id]?.positions ?? null;
      }
      return out;
    }),
  );

  const rows = useMemo(() => {
    type SiblingRow = {
      key: string;
      siblingSlug: string;
      siblingSymbol: string;
      pos: PositionLive;
    };
    const collected: SiblingRow[] = [];
    for (const sp of siblings) {
      const positions = positionsByPair[sp.id];
      if (!positions) continue;
      for (const pos of Object.values(positions)) {
        if (!accountIds.has(pos.accountId)) continue;
        if (pos.lots <= 0) continue;
        collected.push({
          key: `${sp.id}:${pos.accountId}:${pos.ticket}`,
          siblingSlug: sp.slug,
          siblingSymbol: sp.symbol,
          pos,
        });
      }
    }
    return collected.sort(
      (a, b) =>
        a.siblingSymbol.localeCompare(b.siblingSymbol) ||
        a.pos.accountId.localeCompare(b.pos.accountId) ||
        a.pos.ticket - b.pos.ticket,
    );
  }, [siblings, positionsByPair, accountIds]);

  if (siblings.length === 0) return null;

  return (
    <>
      {/* Hidden subscribers — one WS per sibling pair so positions
          flow into the store namespace we read above. */}
      {siblingIds.map((id) => (
        <SiblingSubscriber key={id} pairId={id} />
      ))}
      <AnimatePresence initial={false}>
        {rows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="flex flex-col overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/5"
          >
            <div className="flex items-center justify-between gap-2 border-b border-amber-500/20 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  Open On Sibling Pairs
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {rows.length} live · same legs, different symbol
                </span>
              </div>
            </div>
            <ul className="divide-y divide-amber-500/15">
              <AnimatePresence initial={false}>
                {rows.map((row) => {
                  const p = row.pos;
                  return (
                    <motion.li
                      key={row.key}
                      layout
                      initial={{ opacity: 0, y: -3 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -3 }}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-xs"
                    >
                      <Link
                        to={terminalPath(market, row.siblingSlug)}
                        className="flex items-center gap-1 font-mono font-semibold text-foreground hover:text-accent"
                        title={`Jump to ${row.siblingSymbol} terminal`}
                      >
                        {row.siblingSymbol}
                        <ExternalLink aria-hidden="true" className="size-3 text-muted-foreground" />
                      </Link>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase",
                          p.side === "buy"
                            ? "border-emerald-500/30 text-emerald-500"
                            : "border-rose-500/30 text-rose-500",
                        )}
                      >
                        {p.side}
                      </Badge>
                      <span className="font-mono tabular-nums text-muted-foreground">
                        {p.lots.toFixed(2)} lots
                      </span>
                      <span className="font-mono tabular-nums text-[11px] text-muted-foreground">
                        @ {p.entry}
                      </span>
                      <span className="font-mono tabular-nums text-[11px] text-muted-foreground">
                        #{p.ticket}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {firmNameFor(p.accountId)}
                      </span>
                      <span
                        className={cn(
                          "ml-auto font-mono tabular-nums font-semibold",
                          p.openPnl >= 0 ? "text-emerald-500" : "text-rose-500",
                        )}
                      >
                        {p.openPnl >= 0 ? "+" : ""}
                        {fmtMoney(p.openPnl)}
                      </span>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function SiblingSubscriber({ pairId }: { pairId: string }): ReactNode {
  useHedgeSocket(pairId);
  return null;
}
